import { useEffect, useState } from 'react';
import { Wrench, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { reportError } from '../../lib/errors';
import { useVertical } from '../../context/VerticalContext';
import { clearJobResume, readJobResume, type JobResume } from '../../lib/jobDraft';

interface ResumeJobBannerProps {
    onResume: (jobId: string) => void;
    /** Bump to re-read after the New Job flow closes. */
    refreshKey?: number;
}

/**
 * "Resume job" for a job started from New Job and not yet invoiced, so closing
 * the modal never means hunting for the job in Workshop (lib/jobDraft.ts).
 *
 * The pointer lives on this device only, so the job is checked before it is
 * offered: one deleted, voided or invoiced elsewhere is forgotten instead.
 */
export function ResumeJobBanner({ onResume, refreshKey = 0 }: ResumeJobBannerProps) {
    const { activeOrgId } = useVertical();
    const [resume, setResume] = useState<JobResume | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const pointer = activeOrgId ? readJobResume(activeOrgId) : null;
            if (!activeOrgId || !pointer) {
                if (!cancelled) setResume(null);
                return;
            }
            const [{ data: job, error: jobError }, { data: invoices, error: invoicesError }] = await Promise.all([
                supabase.from('jobs').select('id, status, deleted_at').eq('id', pointer.jobId).maybeSingle(),
                supabase.from('invoices').select('id').eq('job_id', pointer.jobId).is('deleted_at', null).limit(1),
            ]);
            if (jobError) reportError(jobError, 'resume-job-banner.jobs');
            if (invoicesError) reportError(invoicesError, 'resume-job-banner.invoices');
            if (cancelled) return;
            // On a read error, offer it anyway — the flow re-checks on open.
            const finished = !jobError && !invoicesError
                && (!job || job.deleted_at || job.status === 'void' || (invoices?.length ?? 0) > 0);
            if (finished) {
                clearJobResume(activeOrgId);
                setResume(null);
            } else {
                setResume(pointer);
            }
        })();
        return () => { cancelled = true; };
    }, [activeOrgId, refreshKey]);

    if (!resume || !activeOrgId) return null;

    return (
        <div className="flex items-center gap-2 rounded-xl border border-line bg-card px-3 py-2 shadow-card">
            <button
                type="button"
                onClick={() => onResume(resume.jobId)}
                className="flex min-h-[44px] min-w-0 flex-1 items-center gap-3 text-left"
            >
                <Wrench className="h-5 w-5 shrink-0 text-gold" />
                <span className="min-w-0">
                    <span className="block text-sm font-bold text-fg">Resume job</span>
                    {resume.label && <span className="block truncate text-xs text-fg-3">{resume.label}</span>}
                </span>
            </button>
            <button
                type="button"
                onClick={() => { clearJobResume(activeOrgId); setResume(null); }}
                aria-label="Dismiss resume job"
                className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-lg text-fg-3"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    );
}
